import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import {
  useCreateSalesReturnMutation,
  useGetSalesReturnQuery,
} from '../../../store/api/salesApi';

function SalesReturnForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id;
  const [createSalesReturn] = useCreateSalesReturnMutation();
  const { data: customers } = useGetCustomersQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const { data: existingReturn } = useGetSalesReturnQuery(id, {
    skip: !isView,
  });

  const [formData, setFormData] = useState({
    customerId: '',
    returnDate: new Date().toISOString().split('T')[0],
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    salesOrderId: '',
  });

  const [items, setItems] = useState([]);

  const handleAddItem = () => {
    setItems([...items, { productId: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'productId') {
      const product = productsData?.products?.find((p) => p.id === value);
      if (product) {
        newItems[index].unitPrice = product.sellingPrice || 0;
      }
    }
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const vatRate = toNum(company?.vatRate || 0) / 100;
  const vatIncluded = company?.salesVatIncluded !== false;

  const subtotal = items.reduce(
    (sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0),
    0
  );
  const discountTotal = Math.max(0, Math.min(toNum(formData.discountTotal), subtotal));
  const taxable = Math.max(0, subtotal - discountTotal);
  const vatTotal =
    vatRate > 0
      ? vatIncluded
        ? taxable - taxable / (1 + vatRate)
        : taxable * vatRate
      : 0;
  const grandTotal = vatRate > 0 ? (vatIncluded ? taxable : taxable + vatTotal) : taxable;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  useEffect(() => {
    if (existingReturn && isView) {
      setFormData((prev) => ({
        ...prev,
        customerId: existingReturn.customerId || '',
        returnDate: existingReturn.returnDate
          ? existingReturn.returnDate.substring(0, 10)
          : prev.returnDate,
        notes: existingReturn.notes || '',
        discountTotal: existingReturn.discountTotal ?? 0,
        roundOff: existingReturn.roundOff ?? 0,
        salesOrderId: existingReturn.salesOrderId || '',
      }));
      setItems(
        (existingReturn.items || []).map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
        }))
      );
    }
  }, [existingReturn, isView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales/returns');
      return;
    }
    if (!formData.customerId) {
      alert('Customer is required');
      return;
    }
    if (!formData.returnDate) {
      alert('Return Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }

    try {
      await createSalesReturn({
        ...formData,
        discountTotal: toNum(formData.discountTotal),
        roundOff: toNum(formData.roundOff),
        items: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
        })),
      }).unwrap();
      navigate('/sales/returns');
    } catch (error) {
      alert(error?.data?.message || 'Error creating sales return');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isView ? 'Sales Return Details' : 'New Sales Return'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Customer"
                name="customerId"
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                required
              >
                <MenuItem value="">Select Customer</MenuItem>
                {customers?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Return Date"
                name="returnDate"
                value={formData.returnDate}
                onChange={(e) => setFormData({ ...formData, returnDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Return Items
                </Typography>
                {!isView && (
                  <Button startIcon={<AddIcon />} onClick={handleAddItem} variant="outlined">
                    Add Item
                  </Button>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Unit Price (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={item.productId}
                              onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                              sx={{ minWidth: 200 }}
                              disabled={isView}
                            >
                              <MenuItem value="">Select Product</MenuItem>
                              {productsData?.products?.map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                  {product.name} ({product.sku})
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{total.toFixed(2)}</TableCell>
                          {!isView && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                Return Summary (Auto Calculated)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={subtotal.toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={`Discount Total (${currency})`}
                type="number"
                value={formData.discountTotal}
                onChange={(e) => setFormData({ ...formData, discountTotal: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isView}
              />
              <TextField
                fullWidth
                label={`VAT (${currency})`}
                value={vatTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={`Round Off (${currency})`}
                type="number"
                value={formData.roundOff}
                onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ step: 0.01 }}
                disabled={isView}
              />
              <TextField
                fullWidth
                label={`Total Amount (${currency})`}
                value={totalAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/sales/returns')}>
                {isView ? 'Back to List' : 'Cancel'}
              </Button>
              {!isView && (
                <Button variant="contained" type="submit">
                  Save Sales Return
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default SalesReturnForm;

